import React from 'react';
import { Link } from 'react-router-dom';

import { Typography } from '../../../../components/common/Typography/Typography';
import { env } from '../../../../config/env';
import { ModSortBy } from '../../../../constants/enums';
import { EXTERNAL_LINKS } from '../../../../constants/links';
import { APP_ROUTES } from '../../../../constants/routes';
import { useDocumentHead } from '../../../../hooks/useDocumentHead/useDocumentHead';
import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { toCssUrl } from '../../../../services/urlSafety';
import { useGetModsQuery } from '../../../mods/api/modsApi';
import { ModCard } from '../../../mods/components/ModCard/ModCard';
import styles from './HomeScreen.module.css';
import { HomeScreenProps } from './HomeScreen.types';

export const HomeScreen: React.FC<HomeScreenProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const heroBgUrl = `${env().PUBLIC_URL}/images/home/home-hero-bg.jpg`;
  const spotlightBgUrl = EXTERNAL_LINKS.SAIL_TO_UNKNOWN_CONTAINER_BG;

  useDocumentHead({
    title: `${t.brand} - ${t.seo.homeTitle}`,
    description: t.seo.homeDescription,
  });

  const { data: mods, isLoading } = useGetModsQuery({
    sortBy: ModSortBy.UPDATED_AT,
  });

  const featuredMods = mods ? mods.slice(0, 3) : [];

  return (
    <div className={styles.homeWrapper}>
      <div
        className={styles.pageBlurredBackdrop}
        style={{ backgroundImage: toCssUrl(heroBgUrl) }}
        aria-hidden="true"
      />
      <div className={styles.pageBackdropOverlay} aria-hidden="true" />
      <div className={`${styles.container} ${className}`.trim()}>
        {/* 1. Hero Section */}
        <section className={styles.hero} aria-labelledby="hero-title">
          <div
            className={styles.heroBackdrop}
            style={{ backgroundImage: toCssUrl(heroBgUrl) }}
            aria-hidden="true"
          />
          <div className={styles.heroOverlay} aria-hidden="true" />
          <div className={styles.heroGlow} aria-hidden="true" />

          <div className={styles.heroContent}>
            <h1 id="hero-title" className={styles.heroTitle}>
              {t.home.heroTitlePart1}{' '}
              <span className={styles.gradientHighlight}>{t.home.heroTitlePart2}</span>
            </h1>

            <p className={styles.heroSubtitle}>{t.home.heroSubtitle}</p>

            <div className={styles.heroActions}>
              <Link to={APP_ROUTES.MODS} className={styles.primaryCta}>
                {t.home.exploreModsBtn}
              </Link>
              <Link to={APP_ROUTES.GAMES} className={styles.secondaryCta}>
                {t.home.featuredGamesBtn}
              </Link>
            </div>
          </div>
        </section>

        {/* 2. Game Spotlight */}
        <section className={styles.gameSpotlight} aria-labelledby="game-spotlight-heading">
          <div
            className={styles.spotlightBackdrop}
            style={{ backgroundImage: toCssUrl(spotlightBgUrl) }}
            aria-hidden="true"
            data-testid="spotlight-backdrop"
          />
          <div className={styles.spotlightOverlay} aria-hidden="true" />
          <div className={styles.spotlightContent}>
            <span className={styles.spotlightBadge}>{t.home.gameSpotlightBadge}</span>
            <h2 id="game-spotlight-heading" className={styles.spotlightHeading}>
              {t.home.gameSpotlightHeading}
            </h2>
            <p className={styles.spotlightDesc}>{t.home.gameSpotlightDesc}</p>
            <Link to={APP_ROUTES.GAMES} className={styles.spotlightCta}>
              {t.home.gameSpotlightCta}
            </Link>
          </div>
        </section>

        {/* 3. Featured Mods Grid */}
        <section className={styles.section} aria-labelledby="featured-mods-heading">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleGroup}>
              <Typography variant="h2" id="featured-mods-heading">
                {t.home.featuredHeading}
              </Typography>
              <p className={styles.sectionSubtitle}>{t.home.featuredSubtitle}</p>
            </div>
            <Link to={APP_ROUTES.MODS} className={styles.viewAllLink}>
              {t.home.viewAllMods}
            </Link>
          </div>

          {isLoading ? (
            <div className={styles.loadingSkeleton} aria-label={t.home.loadingFeaturedMods}>
              <div className={styles.skeletonCard} />
              <div className={styles.skeletonCard} />
              <div className={styles.skeletonCard} />
            </div>
          ) : (
            <div className={styles.featuredGrid}>
              {featuredMods.map((mod) => (
                <ModCard key={mod.id} mod={mod} />
              ))}
            </div>
          )}

          <div className={styles.browseAllWrapper}>
            <Link to={APP_ROUTES.MODS} className={styles.primaryCta}>
              {t.home.ctaButton}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};
